import React, { useCallback, useMemo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { DollarOutlined } from '@ant-design/icons';
import { Button, Checkbox, Spin, Tooltip } from 'antd';

import type User from 'classes/User';
import Alert from 'components/Alert';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE, MIN_TRANSFER_INTERNATIONAL, MIN_TRANSFER } from 'helpers/constants';
import { tap } from 'rxjs/operators';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import ConfirmTransactionMessage from 'v2/components/Shared/ConfirmTransactionMessage';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Tour from 'v2/components/Shared/Tour';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { useGetFeatureAccess } from 'v2/hooks/useGetFeatureAccess';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import { EFromPageTransfer } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

import { steps } from './tour';

interface IProps {
  user: User;
}

const CardTransferPJ = ({ user }: IProps) => {
  const [loading, setLoading] = useState(false);
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);
  const [transferChecked, setTransferChecked] = useState(false);
  const [anticipationChecked, setAnticipationChecked] = useState(false);
  const [userState, setUserState] = useState({ ...user });
  const permissions = useGetPermissions();
  const { data: registryCheck, shouldBlock, dismissBlock } = useRegistryCheck();

  const minToTransfer = user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;

  const [listAnticipation, error] = useObservable(() => financialService.listAnticipation().pipe(), []);
  const [listWallet, walletError] = useObservable(() => financialService.listAvailable(user?.type).pipe(), []);
  const [bankAccounts, bankAccountsError] = useObservable(
    () => financialService.listBankAccounts({ active: 'true' }).pipe(),
    []
  );

  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const { data: featureAccessData } = useGetFeatureAccess();

  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
  const transferAccessBlocked = featureAccessData?.transfer.status === 'blocked';
  const anticipationAccessBlocked = featureAccessData?.anticipation.status === 'blocked';
  const shouldBlockTransferBtn = userHasBlockTransferBetaModule || transferAccessBlocked;

  const handleCheckTransfer = useCallback(() => setTransferChecked(!transferChecked), [transferChecked]);
  const handleCheckAnticipation = useCallback(
    () => setAnticipationChecked(!anticipationChecked),
    [anticipationChecked]
  );

  const cannotAntecipate = eduzzAccountsService.verifyUserHasBetaModule('cannot-antecipate');

  const totalToTransfer = useMemo(() => {
    const anticipationValue = anticipationChecked ? listWallet?.available_antecipation || 0 : 0;
    const transferValue = transferChecked ? listWallet?.value || 0 : 0;

    return anticipationValue + transferValue;
  }, [anticipationChecked, listWallet, transferChecked]);

  const disableTransferButton = useMemo(() => {
    if (!transferChecked && anticipationChecked) {
      return true;
    }

    if (totalToTransfer < minToTransfer) {
      return true;
    }

    if (shouldBlockTransferBtn) {
      return true;
    }

    return false;
  }, [anticipationChecked, minToTransfer, totalToTransfer, transferChecked, shouldBlockTransferBtn]);

  const code = useMemo(() => (userState?.crossborder ? 'USD' : 'BRL'), [userState]);
  const invoices = useMemo(
    () => (anticipationChecked && listAnticipation?.invoices) || [],
    [anticipationChecked, listAnticipation]
  );

  const commissionsToAnticipate = invoices.flatMap(invoice => invoice.commissions.map(commision => commision.id));

  const bankSelected = useMemo(() => {
    if (bankAccounts?.length) {
      const findAccount = bankAccounts.find(item => item.main);

      if (!findAccount) {
        return bankAccounts[0];
      }

      return findAccount;
    }

    return null;
  }, [bankAccounts]);

  const isLoading = listAnticipation === null && error === undefined;
  const isLoadingBankAccounts = bankAccounts === null && bankAccountsError === undefined;
  const isLoadingListWallet = listWallet === null && walletError === undefined;

  const [sendTransfer] = useCallbackObservable(() => {
    setLoading(true);
    return financialService
      .sendTransfer({
        commissions: commissionsToAnticipate,
        transfer_method_id: bankSelected.id,
        value: totalToTransfer,
        from: EFromPageTransfer.NEW_CARD
      })
      .pipe(
        tap(
          () => {
            toast.success('Sucesso, transferência enviada para processamento com êxito!');
            setLoading(false);
            setUserState({ ...user, has_transfer: true });
          },
          err => {
            setLoading(false);
            toast.error(err);
          }
        )
      );
  }, [totalToTransfer, bankSelected, invoices]);

  const makeTransfer = useCallback(async () => {
    const confirm = await Alert.confirm({
      message: <ConfirmTransactionMessage value={totalToTransfer} bankAccount={bankSelected} />,
      title: 'Atenção',
      buttonOk: 'Confirmar',
      confirmation: true
    });

    if (confirm) {
      sendTransfer();
    }
  }, [bankSelected, sendTransfer, totalToTransfer]);

  const handleSubmit = useCallback(() => {
    if (!transferChecked && !anticipationChecked) {
      toast.error('Selecione os valores para transfêrencia');
      return;
    }

    if (shouldBlock) {
      setShowRegistryBlockModal(true);
      return;
    }

    makeTransfer();
  }, [transferChecked, anticipationChecked, makeTransfer, shouldBlock]);

  const handleCancelRegistryBlockModal = useCallback(() => {
    dismissBlock();
    setShowRegistryBlockModal(false);
    makeTransfer();
  }, [makeTransfer, dismissBlock]);

  const renderTooltipValue = useMemo(() => {
    if (userHasBlockTransferBetaModule) return FINANCIAL_BLOCK_TOOLTIP_MESSAGE;
    if (featureAccessData?.transfer.status === 'blocked') return featureAccessData?.transfer.reason;
    if (!transferChecked && anticipationChecked) return 'Para antecipar é necessário selecionar o saldo a transferir';

    if (totalToTransfer < minToTransfer) {
      return (
        <p>
          Valor mínimo para transferir: <FormattedNumber value={minToTransfer} format={code}></FormattedNumber>
        </p>
      );
    }

    return '';
  }, [
    featureAccessData,
    anticipationChecked,
    code,
    minToTransfer,
    totalToTransfer,
    transferChecked,
    userHasBlockTransferBetaModule
  ]);

  const disableCheckbox = useMemo(() => {
    return (
      isLoadingBankAccounts || !bankSelected || userState?.has_transfer || bankAccountsError || shouldBlockTransferBtn
    );
  }, [bankAccountsError, bankSelected, isLoadingBankAccounts, userState, shouldBlockTransferBtn]);

  const renderTransferButton = useMemo(() => {
    if (isLoadingBankAccounts) return <div />;

    if (!bankSelected) {
      return <div>Você precisa de uma conta bancária cadastrada para transferir</div>;
    }

    if (userState?.has_transfer) {
      return <div>Você tem transferências em processamento</div>;
    }

    if (bankAccountsError) {
      return <div>Erro ao buscar contas bancárias</div>;
    }

    return (
      <div className='flex items-end justify-between'>
        {cannotAntecipate ? (
          <p></p>
        ) : (
          <a
            style={{ fontSize: 14 }}
            href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
            target='_blank'
            rel='noopener noreferrer'
          >
            Entenda a antecipação
          </a>
        )}
        <Tooltip title={renderTooltipValue} placement='top'>
          <div>
            <Button
              className='disabled:bg-grey-100! float-right! bg-[#009358]! text-white! hover:bg-[#007A49]! max-sm:float-none!'
              disabled={loading || disableTransferButton}
              onClick={handleSubmit}
              id='myTransferButton'
              icon={<DollarOutlined style={{ fontSize: 14 }} />}
            >
              Transferir agora
            </Button>
          </div>
        </Tooltip>
      </div>
    );
  }, [
    bankAccountsError,
    bankSelected,
    cannotAntecipate,
    disableTransferButton,
    handleSubmit,
    isLoadingBankAccounts,
    loading,
    renderTooltipValue,
    userState?.has_transfer
  ]);

  const transferBlockedTooltipMessage = () => {
    if (transferAccessBlocked) return featureAccessData?.transfer.reason;
    return '';
  };

  const anticipationBlockedTooltipMessage = () => {
    if (anticipationAccessBlocked) return featureAccessData?.anticipation.reason;
    return '';
  };

  return (
    <>
      <RegistryBlockModal
        opened={showRegistryBlockModal}
        isOwner={permissions?.owned}
        cancelable={registryCheck?.skip}
        onCancel={handleCancelRegistryBlockModal}
      />

      <Tour name='card_transfer_pj_tour' steps={steps} />

      <div id='transfer-card-pj' className='h-full'>
        <div className='flex h-full flex-col justify-between rounded border border-gray-200 bg-white p-4 shadow'>
          <div className='flex justify-between'>
            <div>
              <h4 className='text-xl font-semibold'>Carteira</h4>
              <span className='text-sm font-normal'>Pessoa Jurídica</span>
            </div>

            <div id='transfer-card-pj-values'>
              <div className='flex flex-col'>
                <span className='m-0 text-right text-[16.5px] text-[#586375]'>Disponível para Transferência:</span>

                <span className='m-0 text-right text-[11px] text-[#586375]'>
                  Valor total liberado para ser transferido
                </span>
              </div>

              <div className='flex items-center justify-end'>
                <span className='m-0 text-right text-2xl'>
                  {!isLoadingListWallet ? (
                    <Mask>
                      <FormattedNumber format={code} value={listWallet?.value || 0} />
                    </Mask>
                  ) : (
                    <Spin size='small' />
                  )}
                </span>

                {!isLoading && (
                  <Tooltip className='ml-2' title={transferBlockedTooltipMessage()} placement='top'>
                    <div>
                      <Checkbox disabled={disableCheckbox} checked={transferChecked} onChange={handleCheckTransfer} />
                    </div>
                  </Tooltip>
                )}
              </div>

              {!cannotAntecipate && (
                <>
                  <div className='mt-3 flex flex-col'>
                    <span className='m-0 text-right text-[16.5px] text-[#586375]'>Disponível para Antecipar:</span>

                    <span className='m-0 text-right text-[11px] text-[#586375]'>
                      Valores do saldo futuro que podem ser antecipados
                    </span>
                  </div>
                  <div className='flex items-center justify-end'>
                    <span className='m-0 text-right text-2xl'>
                      {!isLoadingListWallet ? (
                        <Mask>
                          <FormattedNumber format={code} value={listWallet?.available_antecipation || 0} />
                        </Mask>
                      ) : (
                        <Spin size='small' />
                      )}
                    </span>

                    {!isLoading && (
                      <Tooltip className='ml-2' title={anticipationBlockedTooltipMessage()} placement='top'>
                        <div>
                          <Checkbox
                            disabled={disableCheckbox}
                            checked={anticipationChecked}
                            onChange={handleCheckAnticipation}
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <PermissionHide action='myeduzz:dashboard:solicitar-transferencia'>{renderTransferButton}</PermissionHide>
        </div>
      </div>
    </>
  );
};

export default CardTransferPJ;
